import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
import type {
  AssemblyPlan,
  AttachmentPointView,
  GlobalLivery,
} from "../core/types.ts";
import type {
  AssemblyMode,
  AssemblySurface,
  DemonstrationAction,
  SurfaceDebugSnapshot,
} from "./assembly-surface.ts";
import { countMeshTriangles } from "./mesh-metrics.ts";

interface LoadedTemplate {
  readonly scene: THREE.Group;
  readonly animations: readonly THREE.AnimationClip[];
}

interface AtlasTextures {
  readonly baseColor: THREE.Texture;
  readonly normal: THREE.Texture;
  readonly orm: THREE.Texture;
  readonly emissive: THREE.Texture;
}

const LIVERY_BY_ID: Readonly<Record<string, { color: string; preserveBaseMap: boolean }>> = {
  "oxide-red": { color: "#ffffff", preserveBaseMap: true },
  graphite: { color: "#59615e", preserveBaseMap: false },
  field: { color: "#747d55", preserveBaseMap: false },
};

export class ThreeAssemblySurface implements AssemblySurface {
  readonly #container: HTMLElement;
  readonly #scene = new THREE.Scene();
  readonly #camera = new THREE.PerspectiveCamera(42, 1, 0.01, 500);
  readonly #renderer: THREE.WebGLRenderer;
  readonly #controls: OrbitControls;
  readonly #loader = new GLTFLoader();
  readonly #textureLoader = new THREE.TextureLoader();
  readonly #templates = new Map<string, Promise<LoadedTemplate>>();
  readonly #atlasTextures = this.#loadAtlasTextures();
  readonly #pointGeometry = new THREE.SphereGeometry(0.27, 14, 10);
  readonly #pointMaterial = new THREE.MeshBasicMaterial({
    color: 0x5fa69c,
    transparent: true,
    opacity: 0.08,
    depthWrite: false,
  });
  readonly #pointRingGeometry = new THREE.TorusGeometry(0.155, 0.025, 8, 28);
  readonly #pointRingMaterial = new THREE.MeshBasicMaterial({
    color: 0x70b9af,
    transparent: true,
    opacity: 0.68,
    depthTest: false,
    toneMapped: false,
  });
  readonly #selectedRingMaterial = new THREE.MeshBasicMaterial({
    color: 0xf06f4b,
    transparent: true,
    opacity: 1,
    depthTest: false,
    toneMapped: false,
  });
  readonly #pickListeners = new Set<(pointId: string) => void>();
  readonly #pointProxyById = new Map<string, THREE.Mesh>();
  readonly #raycaster = new THREE.Raycaster();
  readonly #pointer = new THREE.Vector2();
  readonly #projectedPoint = new THREE.Vector3();
  readonly #resizeObserver: ResizeObserver;
  #assemblyRoot: THREE.Group | null = null;
  #previewHelper: THREE.BoxHelper | null = null;
  #mixer: THREE.AnimationMixer | null = null;
  #actions: Partial<Record<Exclude<DemonstrationAction, "rest">, THREE.AnimationAction>> = {};
  #activeAction: DemonstrationAction = "rest";
  #selectedPointId: string | null = null;
  #lastPlan: AssemblyPlan | null = null;
  #mode: AssemblyMode = "confirmed";
  #generation = 0;
  #frameRequest = 0;
  #lastFrameTime = performance.now();
  #initialFitDone = false;
  #disposed = false;
  #pointerOrigin: { x: number; y: number } | null = null;
  #assemblySize: [number, number, number] | null = null;

  constructor(container: HTMLElement) {
    this.#container = container;
    this.#renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.#renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.#renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.#renderer.toneMappingExposure = 1.05;
    this.#renderer.setClearColor(0x000000, 0);
    this.#renderer.domElement.setAttribute("aria-hidden", "true");
    this.#container.append(this.#renderer.domElement);

    this.#pointGeometry.userData.shared = true;
    this.#pointRingGeometry.userData.shared = true;

    this.#controls = new OrbitControls(this.#camera, this.#renderer.domElement);
    this.#controls.enableDamping = true;
    this.#controls.dampingFactor = 0.075;
    this.#controls.minDistance = 2;
    this.#controls.maxDistance = 30;
    this.#controls.target.set(0, 1.5, 0);

    addEnvironment(this.#scene);
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this.#container);
    this.#renderer.domElement.addEventListener("pointerdown", this.#onPointerDown);
    this.#renderer.domElement.addEventListener("pointerup", this.#onPointerUp);
    this.#resize();
    this.#camera.position.set(7.5, 5.3, 9);
    this.#frameRequest = requestAnimationFrame(this.#renderFrame);
  }

  async apply(plan: AssemblyPlan, mode: AssemblyMode): Promise<void> {
    this.#assertOpen();
    const generation = ++this.#generation;
    const priorPlan = this.#lastPlan;
    const templates = new Map<string, LoadedTemplate>();
    await Promise.all(
      [...new Set(plan.nodes.map((node) => node.visualRef))].map(async (visualRef) => {
        templates.set(visualRef, await this.#loadTemplate(visualRef));
      }),
    );
    if (this.#disposed || generation !== this.#generation) return;

    const nextRoot = new THREE.Group();
    nextRoot.name = "assembled-mech";
    const positionObjects = new Map<string, THREE.Object3D>();
    const nextPointProxies = new Map<string, THREE.Mesh>();
    let nextMixer: THREE.AnimationMixer | null = null;
    let nextActions: Partial<Record<Exclude<DemonstrationAction, "rest">, THREE.AnimationAction>> = {};

    try {
      for (const node of plan.nodes) {
        const template = templates.get(node.visualRef);
        if (template == null) throw new Error(`Visual template is unavailable: ${node.visualRef}`);
        const object = SkeletonUtils.clone(template.scene);
        captureRestPose(object);
        object.name = node.positionId;
        object.userData.positionId = node.positionId;
        object.userData.moduleId = node.moduleId;
        applyLivery(object, plan.livery);

        if (node.parentPositionId == null) {
          nextRoot.add(object);
          nextMixer = new THREE.AnimationMixer(object);
          nextActions = createAnimationActions(nextMixer, template.animations);
        } else {
          const parent = positionObjects.get(node.parentPositionId);
          if (parent == null) {
            throw new Error(`AssemblyPlan parent is missing: ${node.parentPositionId}`);
          }
          if (node.attachmentPointId == null || node.attachmentAnchor == null) {
            throw new Error(`Child ${node.positionId} has no attachment point`);
          }
          const socket = parent.getObjectByName(node.attachmentAnchor);
          if (socket == null) {
            throw new Error(
              `Required GLB socket is missing: ${node.attachmentAnchor} on ${node.parentPositionId}`,
            );
          }
          socket.add(object);
        }
        positionObjects.set(node.positionId, object);
      }
      addPointProxies(positionObjects, plan, nextPointProxies, {
        hitGeometry: this.#pointGeometry,
        ringGeometry: this.#pointRingGeometry,
        hit: this.#pointMaterial,
        ring: this.#pointRingMaterial,
      });
    } catch (error) {
      disposeAssemblyRoot(nextRoot);
      nextMixer?.stopAllAction();
      throw error;
    }

    this.#replaceAssembly(nextRoot, nextMixer, nextActions, nextPointProxies);
    this.#lastPlan = structuredClone(plan);
    this.#mode = mode;
    this.selectPoint(
      plan.points.find((point) => point.instanceId === this.#selectedPointId) ?? null,
    );

    if (mode === "try-on") this.#showPreviewChange(priorPlan, plan, positionObjects);
    if (this.#activeAction !== "rest" && nextActions[this.#activeAction] == null) {
      this.#activeAction = "rest";
    }
    this.play(this.#activeAction);
    nextRoot.updateMatrixWorld(true);
    nextRoot.traverse((object) => {
      if (object instanceof THREE.SkinnedMesh) object.skeleton.update();
    });
    if (!this.#initialFitDone) {
      this.#fitCamera(nextRoot);
      this.#initialFitDone = true;
    }
  }

  selectPoint(point: AttachmentPointView | null): void {
    this.#assertOpen();
    this.#selectedPointId = point?.instanceId ?? null;
    for (const [pointId, proxy] of this.#pointProxyById) {
      const ring = proxy.children[0] as THREE.Mesh | undefined;
      if (ring == null) continue;
      ring.material = pointId === this.#selectedPointId
        ? this.#selectedRingMaterial
        : this.#pointRingMaterial;
      ring.scale.setScalar(pointId === this.#selectedPointId ? 1.28 : 1);
    }
  }

  projectPointClient(pointId: string): readonly [number, number] | null {
    this.#assertOpen();
    const proxy = this.#pointProxyById.get(pointId);
    if (proxy == null) return null;
    proxy.getWorldPosition(this.#projectedPoint).project(this.#camera);
    const rect = this.#renderer.domElement.getBoundingClientRect();
    return [
      rect.left + ((this.#projectedPoint.x + 1) * 0.5 * rect.width),
      rect.top + ((1 - this.#projectedPoint.y) * 0.5 * rect.height),
    ];
  }

  onPointPick(listener: (pointId: string) => void): () => void {
    this.#assertOpen();
    this.#pickListeners.add(listener);
    return () => this.#pickListeners.delete(listener);
  }

  play(action: DemonstrationAction): boolean {
    this.#assertOpen();
    if (action !== "rest" && this.#actions[action] == null) return false;
    this.#activeAction = action;
    this.#mixer?.stopAllAction();
    restoreCapturedRestPose(this.#assemblyRoot);
    if (action === "rest") return true;
    this.#actions[action]?.reset().setLoop(THREE.LoopRepeat, Infinity).play();
    return true;
  }

  debugSnapshot(): SurfaceDebugSnapshot {
    const programs = Array.isArray(this.#renderer.info.programs)
      ? this.#renderer.info.programs.length
      : null;
    return {
      ready: this.#assemblyRoot != null && !this.#disposed,
      mode: this.#mode,
      nodeCount: this.#lastPlan?.nodes.length ?? 0,
      selectedPointId: this.#selectedPointId,
      activeAction: this.#activeAction,
      availableActions: [
        "rest",
        ...(["idle", "walk"] as const).filter((action) => this.#actions[action] != null),
      ],
      triangles: countMeshTriangles(this.#assemblyRoot),
      cameraPosition: this.#camera.position.toArray() as [number, number, number],
      assemblySize: this.#assemblySize,
      renderer: {
        geometries: this.#renderer.info.memory.geometries,
        textures: this.#renderer.info.memory.textures,
        programs,
      },
    };
  }

  async dispose(): Promise<void> {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#generation += 1;
    cancelAnimationFrame(this.#frameRequest);
    this.#resizeObserver.disconnect();
    this.#renderer.domElement.removeEventListener("pointerdown", this.#onPointerDown);
    this.#renderer.domElement.removeEventListener("pointerup", this.#onPointerUp);
    this.#controls.dispose();
    this.#clearPreviewHelper();
    this.#disposeCurrentAssembly();
    for (const templatePromise of this.#templates.values()) {
      try {
        const template = await templatePromise;
        disposeTemplate(template.scene);
      } catch {
        // A failed asset promise owns no reachable Three resources.
      }
    }
    this.#templates.clear();
    try {
      const atlas = await this.#atlasTextures;
      atlas.baseColor.dispose();
      atlas.normal.dispose();
      atlas.orm.dispose();
      atlas.emissive.dispose();
    } catch {
      // A failed shared atlas owns no reachable GPU resources.
    }
    this.#pointGeometry.dispose();
    this.#pointMaterial.dispose();
    this.#pointRingGeometry.dispose();
    this.#pointRingMaterial.dispose();
    this.#selectedRingMaterial.dispose();
    this.#renderer.renderLists.dispose();
    disposeStaticSceneResources(this.#scene);
    this.#renderer.dispose();
    this.#renderer.forceContextLoss();
    this.#renderer.domElement.remove();
    this.#scene.clear();
    this.#pickListeners.clear();
    this.#pointProxyById.clear();
  }

  readonly #renderFrame = (time: number): void => {
    if (this.#disposed) return;
    const delta = Math.min((time - this.#lastFrameTime) / 1_000, 0.1);
    this.#lastFrameTime = time;
    this.#mixer?.update(delta);
    this.#controls.update();
    this.#renderer.render(this.#scene, this.#camera);
    this.#frameRequest = requestAnimationFrame(this.#renderFrame);
  };

  readonly #onPointerDown = (event: PointerEvent): void => {
    this.#pointerOrigin = { x: event.clientX, y: event.clientY };
  };

  readonly #onPointerUp = (event: PointerEvent): void => {
    const origin = this.#pointerOrigin;
    this.#pointerOrigin = null;
    if (origin == null || Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 7) return;
    const rect = this.#renderer.domElement.getBoundingClientRect();
    this.#pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.#raycaster.setFromCamera(this.#pointer, this.#camera);
    const hit = this.#raycaster.intersectObjects([...this.#pointProxyById.values()], false)[0];
    const pointId = hit?.object.userData.pointInstanceId;
    if (typeof pointId !== "string") return;
    for (const listener of this.#pickListeners) listener(pointId);
  };

  #loadTemplate(visualRef: string): Promise<LoadedTemplate> {
    const existing = this.#templates.get(visualRef);
    if (existing != null) return existing;
    const promise = Promise.all([
      this.#loader.loadAsync(assetUrl(visualRef)),
      this.#atlasTextures,
    ]).then(([gltf, atlas]) => {
      applySharedAtlas(gltf.scene, atlas);
      return {
        scene: gltf.scene,
        animations: gltf.animations,
      };
    });
    this.#templates.set(visualRef, promise);
    return promise;
  }

  async #loadAtlasTextures(): Promise<AtlasTextures> {
    const [baseColor, normal, orm, emissive] = await Promise.all([
      this.#textureLoader.loadAsync(assetUrl("/assets/materials/red-base-color.png")),
      this.#textureLoader.loadAsync(assetUrl("/assets/materials/normal.png")),
      this.#textureLoader.loadAsync(assetUrl("/assets/materials/orm.png")),
      this.#textureLoader.loadAsync(assetUrl("/assets/materials/emissive.png")),
    ]);
    baseColor.colorSpace = THREE.SRGBColorSpace;
    emissive.colorSpace = THREE.SRGBColorSpace;
    for (const texture of [baseColor, normal, orm, emissive]) {
      texture.flipY = false;
      texture.userData.sharedAtlas = true;
      texture.needsUpdate = true;
    }
    return { baseColor, normal, orm, emissive };
  }

  #replaceAssembly(
    root: THREE.Group,
    mixer: THREE.AnimationMixer | null,
    actions: Partial<Record<Exclude<DemonstrationAction, "rest">, THREE.AnimationAction>>,
    pointProxies: ReadonlyMap<string, THREE.Mesh>,
  ): void {
    this.#clearPreviewHelper();
    this.#disposeCurrentAssembly();
    this.#assemblyRoot = root;
    this.#mixer = mixer;
    this.#actions = actions;
    for (const [pointId, proxy] of pointProxies) this.#pointProxyById.set(pointId, proxy);
    this.#scene.add(root);
  }

  #disposeCurrentAssembly(): void {
    this.#mixer?.stopAllAction();
    if (this.#assemblyRoot != null && this.#mixer != null) {
      this.#mixer.uncacheRoot(this.#assemblyRoot);
    }
    this.#mixer = null;
    this.#actions = {};
    this.#pointProxyById.clear();
    if (this.#assemblyRoot != null) {
      this.#assemblyRoot.removeFromParent();
      disposeAssemblyRoot(this.#assemblyRoot);
      this.#assemblyRoot = null;
    }
  }

  #showPreviewChange(
    previous: AssemblyPlan | null,
    next: AssemblyPlan,
    objects: ReadonlyMap<string, THREE.Object3D>,
  ): void {
    const previousModules = new Map(
      previous?.nodes.map((node) => [node.positionId, node.moduleId] as const) ?? [],
    );
    const changed = next.nodes.find(
      (node) => previousModules.get(node.positionId) !== node.moduleId,
    );
    if (changed == null) return;
    const object = objects.get(changed.positionId);
    if (object == null) return;
    this.#previewHelper = new THREE.BoxHelper(object, 0xe4a232);
    this.#previewHelper.material.transparent = true;
    this.#previewHelper.material.opacity = 0.92;
    this.#scene.add(this.#previewHelper);
  }

  #clearPreviewHelper(): void {
    if (this.#previewHelper == null) return;
    this.#previewHelper.removeFromParent();
    this.#previewHelper.geometry.dispose();
    this.#previewHelper.material.dispose();
    this.#previewHelper = null;
  }

  #fitCamera(target: THREE.Object3D): void {
    target.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(target, true);
    if (bounds.isEmpty()) return;
    const center = bounds.getCenter(new THREE.Vector3());
    this.#assemblySize = bounds.getSize(new THREE.Vector3()).toArray() as [number, number, number];
    const radius = Math.max(bounds.getBoundingSphere(new THREE.Sphere()).radius, 0.5);
    const verticalFov = THREE.MathUtils.degToRad(this.#camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov * 0.5) * this.#camera.aspect);
    const distance = (radius / Math.sin(Math.min(verticalFov, horizontalFov) * 0.5)) * 1.14;
    const direction = new THREE.Vector3(1.08, 0.58, 1.34).normalize();
    this.#camera.position.copy(center).addScaledVector(direction, distance);
    this.#camera.near = Math.max(distance / 1_000, 0.01);
    this.#camera.far = Math.max(distance * 20, 100);
    this.#camera.updateProjectionMatrix();
    this.#controls.target.copy(center);
    this.#controls.minDistance = distance * 0.32;
    this.#controls.maxDistance = distance * 5;
    this.#controls.update();
  }

  #resize(): void {
    const width = Math.max(this.#container.clientWidth, 1);
    const height = Math.max(this.#container.clientHeight, 1);
    this.#renderer.setSize(width, height, true);
    this.#camera.aspect = width / height;
    this.#camera.updateProjectionMatrix();
  }

  #assertOpen(): void {
    if (this.#disposed) throw new Error("ThreeAssemblySurface is disposed");
  }
}

function addEnvironment(scene: THREE.Scene): void {
  const hemisphere = new THREE.HemisphereLight(0xdce7e2, 0x1a211e, 2.2);
  const key = new THREE.DirectionalLight(0xfffaf0, 3.25);
  key.position.set(5, 8, 6);
  const fill = new THREE.DirectionalLight(0xa7d4cb, 1.15);
  fill.position.set(-5, 4, -4);
  const grid = new THREE.GridHelper(14, 28, 0x66716d, 0x3b4541);
  const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
  for (const material of gridMaterials) {
    material.transparent = true;
    material.opacity = 0.48;
  }
  grid.userData.environment = true;
  scene.add(hemisphere, key, fill, grid);
}

function captureRestPose(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Bone)) return;
    object.userData.constructorRestPose = {
      position: object.position.toArray(),
      quaternion: object.quaternion.toArray(),
      scale: object.scale.toArray(),
    };
  });
}

function restoreCapturedRestPose(root: THREE.Object3D | null): void {
  if (root == null) return;
  root.traverse((object) => {
    if (!(object instanceof THREE.Bone)) return;
    const pose = object.userData.constructorRestPose as
      | {
          position: [number, number, number];
          quaternion: [number, number, number, number];
          scale: [number, number, number];
        }
      | undefined;
    if (pose == null) return;
    object.position.fromArray(pose.position);
    object.quaternion.fromArray(pose.quaternion);
    object.scale.fromArray(pose.scale);
  });
  root.updateMatrixWorld(true);
  root.traverse((object) => {
    if (object instanceof THREE.SkinnedMesh) object.skeleton.update();
  });
}

function addPointProxies(
  positionObjects: ReadonlyMap<string, THREE.Object3D>,
  plan: AssemblyPlan,
  target: Map<string, THREE.Mesh>,
  resources: {
    hitGeometry: THREE.BufferGeometry;
    ringGeometry: THREE.BufferGeometry;
    hit: THREE.Material;
    ring: THREE.Material;
  },
): void {
  target.clear();
  for (const point of plan.points) {
    const owner = positionObjects.get(point.parentPositionId);
    const socket = owner?.getObjectByName(point.visualAnchor);
    if (socket == null) continue;
    const hit = new THREE.Mesh(resources.hitGeometry, resources.hit);
    hit.name = `point-proxy:${point.instanceId}`;
    hit.userData.constructorHelper = true;
    hit.userData.pointInstanceId = point.instanceId;
    hit.renderOrder = 20;
    const ring = new THREE.Mesh(resources.ringGeometry, resources.ring);
    ring.name = "point-ring";
    ring.userData.constructorHelper = true;
    ring.raycast = () => undefined;
    ring.renderOrder = 21;
    hit.add(ring);
    socket.add(hit);
    target.set(point.instanceId, hit);
  }
}

function createAnimationActions(
  mixer: THREE.AnimationMixer,
  clips: readonly THREE.AnimationClip[],
): Partial<Record<Exclude<DemonstrationAction, "rest">, THREE.AnimationAction>> {
  const actions: Partial<Record<Exclude<DemonstrationAction, "rest">, THREE.AnimationAction>> = {};
  for (const name of ["idle", "walk"] as const) {
    const clip = clips.find((candidate) => candidate.name.toLowerCase() === name);
    if (clip != null) actions[name] = mixer.clipAction(clip);
  }
  return actions;
}

function applySharedAtlas(root: THREE.Object3D, atlas: AtlasTextures): void {
  const discardedMaterials = new Set<THREE.Material>();
  const discardedTextures = new Set<THREE.Texture>();
  const material = new THREE.MeshStandardMaterial({
    name: "mech-shared-atlas",
    color: 0xffffff,
    map: atlas.baseColor,
    normalMap: atlas.normal,
    aoMap: atlas.orm,
    roughnessMap: atlas.orm,
    metalnessMap: atlas.orm,
    emissive: 0xffffff,
    emissiveMap: atlas.emissive,
    roughness: 1,
    metalness: 1,
  });
  material.userData.sharedAtlasMaterial = true;

  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const source = Array.isArray(object.material) ? object.material : [object.material];
    for (const sourceMaterial of source) {
      discardedMaterials.add(sourceMaterial);
      for (const value of Object.values(sourceMaterial)) {
        if (value instanceof THREE.Texture) discardedTextures.add(value);
      }
    }
    object.material = Array.isArray(object.material)
      ? source.map(() => material)
      : material;
  });

  discardedTextures.forEach((texture) => texture.dispose());
  discardedMaterials.forEach((sourceMaterial) => sourceMaterial.dispose());
}

function applyLivery(root: THREE.Object3D, livery: GlobalLivery): void {
  const choice = LIVERY_BY_ID[livery.paletteId] ?? {
    color: livery.primary,
    preserveBaseMap: false,
  };
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const source = Array.isArray(object.material) ? object.material : [object.material];
    const owned = source.map((material) => {
      const clone = material.clone();
      clone.userData.assemblyOwned = true;
      if (clone instanceof THREE.MeshStandardMaterial) {
        clone.color.set(choice.color);
        clone.emissive.set(livery.accent);
        clone.emissiveIntensity = Math.max(0, Math.min(livery.emission, 1));
        if (!choice.preserveBaseMap) clone.map = null;
        clone.needsUpdate = true;
      }
      return clone;
    });
    object.material = Array.isArray(object.material) ? owned : owned[0]!;
  });
}

function disposeAssemblyRoot(root: THREE.Object3D): void {
  const skeletons = new Set<THREE.Skeleton>();
  const ownedMaterials = new Set<THREE.Material>();
  const ownedProxyGeometries = new Set<THREE.BufferGeometry>();
  root.traverse((object) => {
    if (object instanceof THREE.SkinnedMesh) skeletons.add(object.skeleton);
    if (!(object instanceof THREE.Mesh)) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (material.userData.assemblyOwned === true) ownedMaterials.add(material);
    }
    if (object.name.startsWith("point-proxy:") && object.geometry.userData.shared !== true) {
      ownedProxyGeometries.add(object.geometry);
    }
    for (const child of object.children) {
      if (child instanceof THREE.Mesh && child.name === "point-ring" && child.geometry.userData.shared !== true) {
        ownedProxyGeometries.add(child.geometry);
      }
    }
  });
  skeletons.forEach((skeleton) => skeleton.dispose());
  ownedMaterials.forEach((material) => material.dispose());
  ownedProxyGeometries.forEach((geometry) => geometry.dispose());
  root.clear();
}

function disposeTemplate(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  const skeletons = new Set<THREE.Skeleton>();
  root.traverse((object) => {
    if (object instanceof THREE.SkinnedMesh) skeletons.add(object.skeleton);
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of objectMaterials) {
      materials.add(material);
      for (const value of Object.values(material)) {
        if (value instanceof THREE.Texture && value.userData.sharedAtlas !== true) {
          textures.add(value);
        }
      }
    }
  });
  skeletons.forEach((skeleton) => skeleton.dispose());
  textures.forEach((texture) => texture.dispose());
  materials.forEach((material) => material.dispose());
  geometries.forEach((geometry) => geometry.dispose());
  root.clear();
}

function disposeStaticSceneResources(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh || object instanceof THREE.LineSegments)) return;
    geometries.add(object.geometry);
    const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
    objectMaterials.forEach((material) => materials.add(material));
  });
  materials.forEach((material) => material.dispose());
  geometries.forEach((geometry) => geometry.dispose());
}

function assetUrl(path: string): string {
  if (/^(?:https?:|data:|blob:)/.test(path)) return path;
  const base = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  return `${base}${path.replace(/^\/+/, "")}`;
}
